import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import { TProcurementReturns } from 'utils/types';
import * as Yup from 'yup';

interface CancelReturnDetailsFormProps {
  procurementReturnId: number;
}

export type CancelReturnDetailsFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

const CancelReturnDetailsForm = forwardRef<
  CancelReturnDetailsFormHandle,
  CancelReturnDetailsFormProps
>(({ procurementReturnId }, ref) => {
  const [returnDetails, setReturnDetails] = useState({
    cancellationReason: ''
  });
  const { selectedBranch } = useBranchContext();

  const cancelReturnSchema = Yup.object().shape({
    cancellationReason: Yup.string().required('Reason is required')
  });

  const handleSubmit = async () => {
    try {
      await cancelReturnSchema.validate(returnDetails, { abortEarly: false });
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturns/${procurementReturnId}/cancel`,
        { cancellationReason: returnDetails.cancellationReason }
      );
      toast.success('Return Cancelled', {
        autoClose: 2000,
        ...toastConfig
      });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
      }
      return false;
    }
  };

  const handleFormChange = <K extends keyof TProcurementReturns>(
    field: K,
    value: TProcurementReturns[K]
  ) => {
    setReturnDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  return (
    <>
      <Form className="w-100">
        <Row className="mb-3">
          <Form.Label>Cancellation Reason</Form.Label>
          <Form.Control
            type="text"
            value={returnDetails.cancellationReason}
            onChange={e =>
              handleFormChange('cancellationReason', e.target.value)
            }
          />
        </Row>
      </Form>
    </>
  );
});

export default CancelReturnDetailsForm;
